import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

import AddChannelMenu from 'components/admin/add_channel_menu_container';
import Button, { ButtonTypes } from 'components/common/button';
import { H1, H3 } from 'components/common/headers';
import CommunicationQueueEditor, { VoiceConfiguration } from './communication_queue_editor';
import connect from 'components/lib/connect';
import { EndpointTypes } from 'models/endpoint';
import { getRoutingGroupOptions, RoutingGroupOptionsPropTypes } from 'components/lib/assignment_container';
import Ivr from 'models/ivr/ivr';
import PageLayout from 'components/page_layout';
import RealtimeMessagingConfig from 'components/admin/channels/realtime_messaging_config';
import MailConfig from 'components/admin/channels/mail_config';
import UpdateCommunicationQueue from 'actions/channels/update_communication_queue';
import UpdateMessagingConfiguration from 'actions/endpoint/update_messaging_configuration';
import UpdateVoiceConfiguration from 'actions/endpoint/update_voice_configuration';

const EDITABLE_FIELDS = ['name', 'routingGroupId', 'slaSeconds'];

const VOICE_IVRS = 'ivrs';

const REALTIME_CHANNELS = [
  EndpointTypes.SMS,
  EndpointTypes.WHATSAPP,
  EndpointTypes.FB_MESSENGER,
  EndpointTypes.INSTAGRAM_DIRECT,
  EndpointTypes.CHAT,
  EndpointTypes.TWITTER,
];

const CHANNEL_NAMES = Object.freeze({
  [EndpointTypes.CHAT]: 'Chat',
  [EndpointTypes.EMAIL]: 'Email',
  [EndpointTypes.SMS]: 'SMS',
  [EndpointTypes.FB_MESSENGER]: 'FB Messenger',
  [EndpointTypes.INSTAGRAM_DIRECT]: 'Instagram Direct',
  [EndpointTypes.VOICE]: 'Phone',
  [EndpointTypes.WHATSAPP]: 'WhatsApp',
  [EndpointTypes.TWITTER]: 'Twitter',
});

const INITIAL_IVR_ID_PATH = 'configuration.initialIvr.ivrId';
const QUEUE_IVR_ID_PATH = 'configuration.queueIvr.ivrId';

export default class EntryPoints extends PureComponent {
  constructor(props) {
    super(props);

    const { channels = [], ivrs = [], messagingConfiguration = {}, voiceConfiguration = {} } = props;

    this.state = {
      channels,
      ivrs,
      messagingConfiguration,
      voiceConfiguration,
    };

    this.voiceIvrRef = React.createRef();
    this.scrollToVoiceIvr = _.once(this.scrollTo.bind(this, this.voiceIvrRef));

    _.bindAll(this, ['editQueue', 'editMessagingConfiguration', 'editVoiceConfiguration', 'onSaveClick']);
  }

  UNSAFE_componentWillReceiveProps(props) {
    // TODO: consider how backend updates should affect unsaved edits
    if (!props.isPending) {
      this.setState({
        channels: props.channels,
        messagingConfiguration: props.messagingConfiguration,
        voiceConfiguration: props.voiceConfiguration,
      });

      if (props.sectionToScroll === VOICE_IVRS && !_.isEmpty(this.voiceIvrRef.current)) {
        this.scrollToVoiceIvr();
      }
    }
  }

  scrollTo(ref) {
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  render() {
    return (
      <PageLayout
        className="entryPoints-container"
        childrenContainerClassName="entryPoints"
        showLoading={this.props.isPending}
      >
        <div className="entryPoints-main">
          {this.renderHeader()}
          {this.renderChannels()}
        </div>
        {this.renderButtons()}
      </PageLayout>
    );
  }

  renderHeader() {
    return (
      <div className="entryPoints-header">
        <H1 data-aid="entryPoints-header-title">Channels</H1>
        <AddChannelMenu className="entryPoints-header-addChannelMenu" title="Add Entry Point" />
      </div>
    );
  }

  renderVoiceChannels() {
    const allVoiceQueues = _.filter(this.state.channels, channel => channel.type === EndpointTypes.VOICE);

    if (_.isEmpty(allVoiceQueues)) {
      return null;
    }

    const [ivrQueues, voiceQueues] = _.partition(allVoiceQueues, voiceChannel => _.has(voiceChannel, 'ivrGroup'));
    const scrollAnchor = _.isEmpty(ivrQueues) ? null : <ScrollAnchor ref={this.voiceIvrRef} />;

    return (
      <div id="entryPoints-channels-voice" key="voice">
        <H3>{CHANNEL_NAMES[EndpointTypes.VOICE]}</H3>
        {scrollAnchor}
        {this.renderVoiceConfiguration(allVoiceQueues)}
        {this.renderQueuesForChannel(voiceQueues)}
        {this.renderIvrSection(ivrQueues)}
      </div>
    );
  }

  renderIvrSection(ivrQueues) {
    if (_.isEmpty(ivrQueues)) {
      return null;
    }

    const renderedIvrQueues = _.chain(ivrQueues)
      .groupBy('ivrGroup')
      .map((ivrGroupQueues, ivrGroup) => (
        <div className=".entryPoints-ivr-channels" key={`ivr-${ivrGroup}`}>
          <H3>{ivrGroup}</H3>
          <p>
            These numbers are exit nodes that are within {ivrGroup}. The Exit Node is tied to the number associated with
            the IVR. Please map which inbox this will go to and set the SLA for the inbox.
          </p>
          {this.renderQueuesForChannel(ivrGroupQueues)}
        </div>
      ))
      .value();

    return (
      <div id="entryPoints-channels-ivrs">
        <ScrollAnchor ref={this.voiceIvrRef} />
        {renderedIvrQueues}
      </div>
    );
  }

  renderVoiceConfiguration(voiceQueues) {
    return _.isEmpty(voiceQueues) ? null : (
      <VoiceConfiguration
        editVoiceConfiguration={this.editVoiceConfiguration}
        voiceConfiguration={this.state.voiceConfiguration}
      />
    );
  }

  renderChannels() {
    const realtimeChannels = REALTIME_CHANNELS.map(channel => this.renderChannel(channel));

    return (
      <ChannelsBlock className="entryPoints-channels">
        {this.renderMailSection()}
        {this.renderChannel(EndpointTypes.EMAIL)}
        {this.renderVoiceChannels()}
        {this.renderRealtimeMessagingSection()}
        {realtimeChannels}
      </ChannelsBlock>
    );
  }

  renderRealtimeMessagingSection() {
    const isAnyRealtimeChannelConfigured = _.some(
      this.props.channels,
      channel => REALTIME_CHANNELS.indexOf(channel.type) !== -1
    );

    return isAnyRealtimeChannelConfigured ? (
      <RealtimeMessagingConfig
        configuration={this.state.messagingConfiguration}
        updateEditedConfigValue={this.editMessagingConfiguration}
      />
    ) : null;
  }

  editMessagingConfiguration({ key, value }) {
    this.setState(prevState => {
      const messagingConfiguration = _.cloneDeep(prevState.messagingConfiguration);
      messagingConfiguration[key] = value;
      return { messagingConfiguration };
    });
  }

  renderMailSection() {
    if (!this.state.messagingConfiguration) {
      return null;
    }

    return (
      <MailConfig
        assignMailToConversationAssignee={this.state.messagingConfiguration.assignMailToConversationAssignee}
        endMailSessionIntervalMins={this.state.messagingConfiguration.endMailSessionIntervalMins}
        updateEditedConfigValue={this.editMessagingConfiguration}
      />
    );
  }

  renderChannel(channel) {
    const queues = this.state.channels.filter(c => c.type === channel);
    if (queues.length) {
      return (
        <div id={`entryPoints-channels-${channel.toLowerCase()}`} key={channel}>
          <H3>{CHANNEL_NAMES[channel]}</H3>
          {this.renderQueuesForChannel(queues)}
        </div>
      );
    }
  }

  renderQueuesForChannel(queues = []) {
    const queuesComponents = queues.map((q, index) => (
      <CommunicationQueueEditor
        key={q.id}
        {...q}
        editQueue={this.editQueue}
        editVoiceConfiguration={this.editVoiceConfiguration}
        renderHeader={index === 0}
        routingGroupOptions={this.props.routingGroupOptions}
        routingGroupSlaSeconds={this.props.groupsSlaSeconds[q.routingGroupId]}
        voiceConfiguration={this.state.voiceConfiguration}
      />
    ));

    return <div className="entryPoints-channels-queues">{queuesComponents}</div>;
  }

  renderButtons() {
    // TODO: Add error handling (see rules page)
    return (
      <div className="entryPoints-buttons">
        <Button className="entryPoints-saveButton" buttonType={ButtonTypes.PRIMARY} onClick={this.onSaveClick}>
          Save
        </Button>
      </div>
    );
  }

  editQueue({ id, value, key }) {
    this.setState(prevState => {
      const channels = _.cloneDeep(prevState.channels);
      let channel = channels.find(c => c.id === id);
      channel[key] = value;
      return { channels };
    });
  }

  editVoiceConfiguration({ key, value }) {
    this.setState(prevState => {
      const voiceConfiguration = _.cloneDeep(prevState.voiceConfiguration);
      voiceConfiguration[key] = value;
      return { voiceConfiguration };
    });
  }

  onSaveClick() {
    // TODO: Add error handling (see rules page)

    // mark changes that were made
    let dirtyQueues = [];

    this.state.channels.forEach(c => {
      let original = this.props.channels.find(ch => ch.id === c.id);
      let queueChanges = { communicationQueueId: c.id };
      EDITABLE_FIELDS.forEach(field => {
        if (c[field] !== original[field]) {
          queueChanges[field] = c[field];
        }
      });
      if (Object.keys(queueChanges).length > 1) {
        dirtyQueues.push(queueChanges);
      }
    });

    // save channel changes
    dirtyQueues.forEach(this.props.updateQueue);

    if (this.state.messagingConfiguration !== this.props.messagingConfiguration) {
      this.props.updateMessagingConfiguration(this.state.messagingConfiguration);
    }

    if (this.state.voiceConfiguration !== this.props.voiceConfiguration) {
      this.props.updateVoiceConfiguration(this.state.voiceConfiguration);
    }
  }
}

export const ChannelsBlock = styled.div`
  margin-bottom: 80px;
`;

export const ScrollAnchor = styled.div`
  position: relative;
  top: -80px; // compensation to scroll to the section header
  left: 0;
`;

EntryPoints.propTypes = {
  channels: PropTypes.array,
  groupsSlaSeconds: PropTypes.object,
  isPending: PropTypes.bool,
  ivrs: PropTypes.arrayOf(PropTypes.instanceOf(Ivr)),
  messagingConfiguration: PropTypes.object,
  routingGroupOptions: RoutingGroupOptionsPropTypes,
  updateMessagingConfiguration: PropTypes.func.isRequired,
  updateQueue: PropTypes.func.isRequired,
  updateVoiceConfiguration: PropTypes.func.isRequired,
  voiceConfiguration: PropTypes.object,
};

export const EntryPointsContainer = connect(mapStateToProps, mapExecuteToProps)(EntryPoints);

function mapStateToProps(props) {
  const { getProvider, isFeatureEnabled } = props;
  getProvider('accounts'); // observe changes to fb accounts
  const messagingConfiguration = getProvider('messagingConfiguration').get();
  const voiceConfiguration = getProvider('voiceConfiguration').getPending() || getProvider('voiceConfiguration').get();
  const routingGroupsProvider = getProvider('routingGroups');
  const agentsProvider = getProvider('agents');
  const ivrs = getIvrs();

  const channels = getChannels();
  const sectionToScroll = _.get(getProvider('currentLocation').get(), 'section');

  return {
    channels,
    groupsSlaSeconds: getGroupsSlaSeconds(),
    isPending: isPending(),
    ivrs,
    messagingConfiguration,
    routingGroupOptions: getRoutingGroupOptions({ routingGroupsProvider, isFeatureEnabled, agentsProvider }),
    sectionToScroll,
    voiceConfiguration,
  };

  function getIvrs() {
    return _.map(getProvider('ivrs').findAll(), ivr => _.pick(ivr, ['id', 'name', 'description']));
  }

  function getGroupsSlaSeconds() {
    const routingGroups = getProvider('routingGroups').findAll();
    const groupsSlaSeconds = {};
    routingGroups.forEach(group => {
      groupsSlaSeconds[group.id] = group.slaSeconds;
    });
    return groupsSlaSeconds;
  }

  function isIvrEndpoint(endpoint) {
    return (
      endpoint.type === EndpointTypes.VOICE &&
      (_.has(endpoint, INITIAL_IVR_ID_PATH) || _.has(endpoint, QUEUE_IVR_ID_PATH))
    );
  }

  function getChannelAddress(channelAddress, endpoint, queue) {
    if (endpoint.type === EndpointTypes.FB_MESSENGER) {
      return endpoint.displayName;
    }

    if (endpoint.type === EndpointTypes.INSTAGRAM_DIRECT) {
      return endpoint.displayName;
    }

    if (isIvrEndpoint(endpoint) && queue.routingSelector) {
      return `${queue.routingSelector}, ${channelAddress}`;
    }

    if ((endpoint.type === EndpointTypes.CHAT || endpoint.type === EndpointTypes.VOICE) && queue.routingSelector) {
      return `${channelAddress}, ${queue.routingSelector}`;
    }

    return channelAddress;
  }

  function getChannels() {
    const communicationQueues = getProvider('communicationQueues').findAll();
    const channelConfiguration = getProvider('channelConfiguration').get();
    const endpoints = _(channelConfiguration).get('endpoints', []);
    const ivrs = getIvrs();

    return _.reduce(
      communicationQueues,
      (memo, queue) => {
        const endpoint = endpoints.find(ep => ep.id === queue.endpointId);

        if (!endpoint) {
          return memo;
        }

        const channel = { ...endpoint, ...queue };

        if (isIvrEndpoint(endpoint)) {
          const endpointId = _.get(endpoint, INITIAL_IVR_ID_PATH) || _.get(endpoint, QUEUE_IVR_ID_PATH);
          const ivrForEndpoint = _.find(ivrs, ivr => ivr.id === endpointId);

          if (ivrForEndpoint) {
            channel.ivrGroup =
              ivrForEndpoint.name || ivrForEndpoint.description || ivrForEndpoint.id || channel.address;
          }
        }

        channel.address = getChannelAddress(channel.address, endpoint, queue);

        memo.push(_.omit(channel, ['configuration', 'displayName', 'routingSelector']));

        return memo;
      },
      []
    );
  }

  function isPending() {
    const channelConfig = getProvider('channelConfiguration');
    const queues = getProvider('communicationQueues');
    const messagingConfig = getProvider('messagingConfiguration');
    const voiceConfig = getProvider('voiceConfiguration');
    const ivrs = getProvider('ivrs');

    const isQueuePending = queues.findAll().some(c => queues.isPending(c.id));
    const isIvrPending = ivrs.isLoading();
    const isChannelsPending = [channelConfig, messagingConfig, voiceConfig].some(channel => channel.isPending());

    return isQueuePending || isIvrPending || isChannelsPending;
  }
}

function mapExecuteToProps(executeAction) {
  return {
    updateMessagingConfiguration: props => executeAction(UpdateMessagingConfiguration, props),
    updateQueue: props => executeAction(UpdateCommunicationQueue, props),
    updateVoiceConfiguration: props => executeAction(UpdateVoiceConfiguration, props),
  };
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/entry_points.jsx